import { extractPdfPageTexts } from './RRDPdfTextExtractor';

export type ClassifiedDocumentType =
  | 'Appeal Letter'
  | 'Cover Letter'
  | 'Payer Letter'
  | 'Patient Letter'
  | 'Medical Record'
  | 'IZ Statement'
  | 'Ambulance Report'
  | 'Rate Sheet'
  | 'W9 Form'
  | 'EOB'
  | 'PT Statement'
  | 'Charity Denial'
  | 'Charity Denial Over Income'
  | 'Other';

export type DocumentTypeClassification = {
  documentType: ClassifiedDocumentType;
  confidence: number;
  scores: Partial<Record<ClassifiedDocumentType, number>>;
  evidence: string[];
};

type DocumentTypeRule = {
  documentType: ClassifiedDocumentType;
  weight: number;
  evidence: string;
  pattern: RegExp;
};

const DOCUMENT_TYPE_RULES: DocumentTypeRule[] = [
  { documentType: 'Appeal Letter', weight: 9, evidence: 'appeal terminology', pattern: /\b(?:appeal|appealing|reconsideration|grievance|adverse\s+determination|letter\s+of\s+appeal|request\s+for\s+appeal)\b/i },
  { documentType: 'Appeal Letter', weight: 7, evidence: 'denial challenge wording', pattern: /\b(?:denial|denied|deny)\b.{0,80}\b(?:claim|payment|coverage|authorization|services?)\b/i },
  { documentType: 'Appeal Letter', weight: 6, evidence: 'review request wording', pattern: /\b(?:request(?:ing)?|please)\b.{0,80}\b(?:review|reprocess|overturn|reverse)\b/i },

  { documentType: 'Cover Letter', weight: 8, evidence: 'cover letter label', pattern: /\bcover\s+letter\b/i },
  { documentType: 'Cover Letter', weight: 6, evidence: 'enclosed/attached submission wording', pattern: /\b(?:enclosed|attached|please\s+find|included)\b.{0,80}\b(?:documents?|records?|forms?|information|documentation)\b/i },
  { documentType: 'Cover Letter', weight: 4, evidence: 'introductory routing wording', pattern: /\b(?:to\s+whom\s+it\s+may\s+concern|submitted\s+for\s+processing|for\s+your\s+review)\b/i },

  { documentType: 'Payer Letter', weight: 8, evidence: 'payer decision or notice wording', pattern: /\b(?:notice\s+of\s+(?:action|determination|decision)|determination\s+letter|coverage\s+determination|claim\s+decision|payment\s+determination)\b/i },
  { documentType: 'Payer Letter', weight: 6, evidence: 'payer response wording', pattern: /\b(?:we\s+(?:reviewed|received|processed)|this\s+letter\s+is\s+to\s+inform\s+you)\b.{0,100}\b(?:claim|request|appeal|authorization|coverage)\b/i },
  { documentType: 'Payer Letter', weight: 5, evidence: 'insurance organization wording', pattern: /\b(?:health\s+plan|insurance\s+company|member\s+services|provider\s+services|claims\s+department)\b/i },

  { documentType: 'Patient Letter', weight: 8, evidence: 'patient-facing letter wording', pattern: /\b(?:dear\s+patient|patient\s+letter|dear\s+(?:member|sir|madam))\b/i },
  { documentType: 'Patient Letter', weight: 6, evidence: 'patient address or balance wording', pattern: /\b(?:your\s+(?:account|balance|bill|visit|care|medical\s+record)|please\s+contact\s+(?:our\s+)?(?:office|billing))\b/i },
  { documentType: 'Patient Letter', weight: 5, evidence: 'patient responsibility wording', pattern: /\bpatient\s+responsibility\b/i },

  { documentType: 'Medical Record', weight: 8, evidence: 'clinical record section', pattern: /\b(?:discharge\s+summary|history\s+and\s+physical|operative\s+report|progress\s+note|consultation\s+note|clinical\s+note|medical\s+record)\b/i },
  { documentType: 'Medical Record', weight: 6, evidence: 'clinical findings wording', pattern: /\b(?:chief\s+complaint|assessment\s+and\s+plan|physical\s+exam|review\s+of\s+systems|vital\s+signs|diagnosis|medications?|allergies)\b/i },
  { documentType: 'Medical Record', weight: 5, evidence: 'lab/radiology wording', pattern: /\b(?:laboratory\s+results?|radiology\s+report|pathology\s+report|imaging\s+study|test\s+results?)\b/i },

  { documentType: 'IZ Statement', weight: 9, evidence: 'itemized statement label', pattern: /\b(?:itemized\s+statement|itemized\s+bill|charge\s+detail|statement\s+of\s+charges|detailed\s+bill|hospital\s+bill)\b/i },
  { documentType: 'IZ Statement', weight: 7, evidence: 'line-level billing codes', pattern: /\b(?:CPT|HCPCS|revenue\s+code|rev\s+code|procedure\s+code|service\s+code)\b/i },
  { documentType: 'IZ Statement', weight: 6, evidence: 'billing charge columns', pattern: /\b(?:date\s+of\s+service|service\s+date|description)\b.{0,120}\b(?:charges?|amount|qty|quantity|total\s+charges)\b/i },

  { documentType: 'Ambulance Report', weight: 10, evidence: 'ambulance report label', pattern: /\b(?:ambulance\s+(?:report|run|transport)|ems\s+(?:report|run)|patient\s+care\s+report|ePCR|PCR\s+number)\b/i },
  { documentType: 'Ambulance Report', weight: 7, evidence: 'transport details', pattern: /\b(?:paramedic|EMT|unit\s+dispatched|dispatch\s+time|scene\s+time|transported\s+to|pickup\s+location|destination\s+facility|mileage)\b/i },
  { documentType: 'Ambulance Report', weight: 5, evidence: 'ambulance clinical narrative', pattern: /\b(?:loaded\s+(?:patient|pt)|stretcher|lights\s+and\s+sirens|chief\s+complaint)\b/i },

  { documentType: 'Rate Sheet', weight: 10, evidence: 'rate sheet label', pattern: /\b(?:rate\s+sheet|fee\s+schedule|rate\s+schedule|reimbursement\s+schedule|contracted\s+rates?)\b/i },
  { documentType: 'Rate Sheet', weight: 7, evidence: 'rate/payment table columns', pattern: /\b(?:CPT|HCPCS|revenue\s+code|procedure\s+code)\b.{0,120}\b(?:rate|allowed\s+amount|contracted\s+rate|fee|reimbursement)\b/i },
  { documentType: 'Rate Sheet', weight: 5, evidence: 'effective rate wording', pattern: /\b(?:effective\s+date|expiration\s+date|per\s+diem|case\s+rate|DRG\s+rate|negotiated\s+rate)\b/i },

  { documentType: 'W9 Form', weight: 10, evidence: 'W-9 form label', pattern: /\b(?:Form\s+W-?9|W-?9\s+Form|Request\s+for\s+Taxpayer\s+Identification\s+Number\s+and\s+Certification)\b/i },
  { documentType: 'W9 Form', weight: 8, evidence: 'taxpayer identification wording', pattern: /\b(?:taxpayer\s+identification\s+number|TIN|employer\s+identification\s+number|EIN|social\s+security\s+number|SSN)\b/i },
  { documentType: 'W9 Form', weight: 6, evidence: 'W-9 certification wording', pattern: /\b(?:backup\s+withholding|exempt\s+payee\s+code|FATCA\s+reporting|certification\s+instructions|Internal\s+Revenue\s+Service)\b/i },

  { documentType: 'EOB', weight: 10, evidence: 'EOB label', pattern: /\b(?:explanation\s+of\s+benefits|EOB)\b/i },
  { documentType: 'EOB', weight: 8, evidence: 'not a bill wording', pattern: /\bthis\s+is\s+not\s+a\s+bill\b/i },
  { documentType: 'EOB', weight: 7, evidence: 'claim payment columns', pattern: /\b(?:amount\s+billed|billed\s+amount|allowed\s+amount|amount\s+allowed|amount\s+paid|paid\s+amount|plan\s+paid|patient\s+responsibility)\b/i },
  { documentType: 'EOB', weight: 5, evidence: 'adjustment/deductible wording', pattern: /\b(?:deductible|coinsurance|copay|co-pay|adjustment\s+(?:code|reason)|remark\s+code|claim\s+number)\b/i },

  { documentType: 'PT Statement', weight: 10, evidence: 'patient statement label', pattern: /\b(?:patient\s+statement|PT\s+statement|account\s+statement)\b/i },
  { documentType: 'PT Statement', weight: 7, evidence: 'balance/amount due wording', pattern: /\b(?:balance\s+due|amount\s+due|total\s+due|current\s+balance|statement\s+balance|minimum\s+payment\s+due)\b/i },
  { documentType: 'PT Statement', weight: 5, evidence: 'statement payment wording', pattern: /\b(?:statement\s+date|due\s+date|please\s+(?:remit|pay)|payment\s+enclosed|detach\s+and\s+return)\b/i },
];

const DOCUMENT_TYPE_TIE_BREAK_ORDER: ClassifiedDocumentType[] = [
  'Appeal Letter',
  'EOB',
  'IZ Statement',
  'Ambulance Report',
  'W9 Form',
  'Rate Sheet',
  'Medical Record',
  'Payer Letter',
  'Patient Letter',
  'PT Statement',
  'Charity Denial Over Income',
  'Charity Denial',
  'Cover Letter',
  'Other',
];

function countPatternMatches(text: string, pattern: RegExp): number {
  const flags = pattern.flags.includes('g') ? pattern.flags : `${pattern.flags}g`;
  const globalPattern = new RegExp(pattern.source, flags);
  return Array.from(text.matchAll(globalPattern)).length;
}

export function classifyDocumentText(fullText: string): DocumentTypeClassification {
  const scores: Partial<Record<ClassifiedDocumentType, number>> = {};
  const evidenceByType: Partial<Record<ClassifiedDocumentType, string[]>> = {};

  for (const rule of DOCUMENT_TYPE_RULES) {
    const matches = countPatternMatches(fullText, rule.pattern);
    if (matches === 0) continue;

    const cappedMatches = Math.min(matches, 3);
    scores[rule.documentType] = (scores[rule.documentType] || 0) + rule.weight * cappedMatches;
    evidenceByType[rule.documentType] = [
      ...(evidenceByType[rule.documentType] || []),
      `${rule.evidence}${matches > 1 ? ` (${matches})` : ''}`,
    ];
  }

  const ranked = DOCUMENT_TYPE_TIE_BREAK_ORDER
    .map((documentType) => ({ documentType, score: scores[documentType] || 0 }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || DOCUMENT_TYPE_TIE_BREAK_ORDER.indexOf(a.documentType) - DOCUMENT_TYPE_TIE_BREAK_ORDER.indexOf(b.documentType));

  if (ranked.length === 0) {
    return {
      documentType: 'Other',
      confidence: 0.2,
      scores,
      evidence: [],
    };
  }

  const best = ranked[0];
  const secondScore = ranked[1]?.score || 0;
  const totalScore = ranked.reduce((sum, entry) => sum + entry.score, 0);
  const confidence = Math.min(0.98, Math.max(0.55, (best.score / Math.max(totalScore, 1)) * 0.7 + ((best.score - secondScore) / Math.max(best.score, 1)) * 0.3));

  return {
    documentType: best.documentType,
    confidence: Number(confidence.toFixed(2)),
    scores,
    evidence: evidenceByType[best.documentType] || [],
  };
}

/**
 * Classifies a document from already-extracted page texts (no PDF parsing).
 * Scores all pages together so the strongest document-level evidence wins.
 */
export function classifyPageTexts(pageTexts: string[]): DocumentTypeClassification {
  const combinedText = pageTexts.join('\n\n');
  return classifyDocumentText(combinedText);
}

/**
 * Classifies the whole PDF by collecting text from every page, then scoring all
 * pages together so the strongest document-level evidence wins.
 *
 * Prefer `classifyPageTexts` (via `analyzePdfDocument`) when field extraction
 * is also needed for the same file — this wrapper re-parses the PDF from
 * scratch and is kept for standalone/back-compat use.
 */
export async function classifyPDFDocumentType(file: File): Promise<DocumentTypeClassification> {
  const pageTexts = await extractPdfPageTexts(file);
  return classifyPageTexts(pageTexts.map((p) => p.fullText));
}
